import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/api';
import { TokenStorageService } from 'src/app/shared/token-storage';
import { CommonService } from '../shared/common.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'dashboard-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  form: any = {
    email: '',
    lost: false,
    password: '',
    code: '',
  };
  isLoggedIn = false;
  isLoginFailed = false;
  errorMessage = '';
  roles: string[] = [];

  error = false;
  isSubmitting = false;
  requiresTotp = false;
  requiresTotpSetup = false;
  setupReady = false;
  pendingToken = '';
  setupToken = '';
  qrCodeDataUrl = '';
  otpauthUri = '';
  expiresInSeconds = 0;
  totpResetMode = false;
  totpResetCompleted = false;
  resetRequested = false;
  resetMessage = '';
  version = '';
  private expiryTimer: any;

  constructor(
    public commonService: CommonService,
    private apiService: ApiService,
    private tokenStorage: TokenStorageService,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    this.version = environment.version;
  }

  ngOnInit() {
    const resetToken = this.route.snapshot.paramMap.get('token');
    if (resetToken) {
      this.totpResetMode = true;
      this.requiresTotpSetup = true;
      this.setupToken = resetToken;
      this.startExpiryCountdown(600);
      this.loadTotpSetup();
      return;
    }

    if (this.tokenStorage.getToken()) {
      this.isLoggedIn = true;
      this.roles = this.tokenStorage.getUser()?.backendRoles || [];
    }
  }

  ngOnDestroy() {
    this.clearExpiryTimer();
  }

  sendLoginForm() {
    this.resetError();
    this.isSubmitting = true;
    const { email, password } = this.form;
    this.apiService.login(email, password).subscribe(
      (data: any) => {
        this.isSubmitting = false;
        this.form.password = '';

        if (data.status === 'TOTP_SETUP_REQUIRED') {
          this.requiresTotpSetup = true;
          this.setupToken = data.setupToken;
          this.startExpiryCountdown(data.expiresInSeconds || 600);
          this.loadTotpSetup();
          return;
        }

        if (data.status === 'TOTP_REQUIRED') {
          this.requiresTotp = true;
          this.pendingToken = data.pendingToken;
          this.startExpiryCountdown(data.expiresInSeconds || 600);
          return;
        }

        this.completeLogin(data);
      },
      err => {
        this.isSubmitting = false;
        this.error = true;
        this.errorMessage = this.getLoginErrorMessage(err);
      }
    );
  }

  private loadTotpSetup() {
    this.apiService.setupTotp(this.setupToken).subscribe(
      (data: any) => {
        this.setupReady = true;
        this.qrCodeDataUrl = data.qrCodeDataUrl;
        this.otpauthUri = data.otpauthUri;
      },
      err => this.showTotpError(err, 'Der Authenticator-Setup-Code ist abgelaufen. Bitte melden Sie sich erneut an.')
    );
  }

  enableTotp() {
    this.normalizeCode();
    if (!this.hasValidCode()) {
      this.showTotpError(null, 'Der Authenticator-Code muss 6 Ziffern enthalten.');
      return;
    }

    this.submitTotp(() => this.apiService.enableTotp(this.setupToken, this.form.code), data => {
      this.requiresTotpSetup = false;
      this.setupReady = false;
      this.clearExpiryTimer();
      if (this.totpResetMode) {
        this.totpResetCompleted = true;
        this.resetMessage = 'Die 2FA wurde erfolgreich neu eingerichtet. Bitte melden Sie sich erneut an.';
        return;
      }
      this.completeLogin(data);
    });
  }

  verifyTotp() {
    this.normalizeCode();
    if (!this.hasValidCode()) {
      this.showTotpError(null, 'Der Authenticator-Code muss 6 Ziffern enthalten.');
      return;
    }

    this.submitTotp(() => this.apiService.verifyTotp(this.pendingToken, this.form.code), data => {
      this.completeLogin(data);
    });
  }

  resetTotp() {
    if (!this.pendingToken || this.isSubmitting || this.expiresInSeconds <= 0) {
      return;
    }

    this.resetError();
    this.isSubmitting = true;
    this.apiService.resetTotp(this.pendingToken).subscribe(
      () => {
        this.isSubmitting = false;
        this.requiresTotp = false;
        this.pendingToken = '';
        this.resetRequested = true;
        this.resetMessage = 'Ein Link zum Zurücksetzen der 2FA wurde per E-Mail gesendet. Der Link ist 10 Minuten gültig.';
        this.clearExpiryTimer();
      },
      err => this.showTotpError(err, 'Der Link zum Zurücksetzen der 2FA konnte nicht gesendet werden.')
    );
  }

  normalizeCode() {
    this.form.code = `${this.form.code || ''}`.replace(/\D/g, '').slice(0, 6);
  }

  hasValidCode() {
    return /^\d{6}$/.test(this.form.code || '');
  }

  backToPasswordLogin() {
    const resetMode = this.totpResetMode;
    this.requiresTotp = false;
    this.requiresTotpSetup = false;
    this.setupReady = false;
    this.pendingToken = '';
    this.setupToken = '';
    this.qrCodeDataUrl = '';
    this.otpauthUri = '';
    this.form.code = '';
    this.totpResetMode = false;
    this.totpResetCompleted = false;
    this.resetRequested = false;
    this.resetMessage = '';
    this.resetError();
    this.clearExpiryTimer();
    if (resetMode) {
      this.router.navigateByUrl('/login');
    }
  }

  resetPasswordForm() {
    const { email } = this.form;
    this.apiService.requestPasswordReset(email).subscribe(
      () => this.reloadPage(),
      () => {
        this.error = true;
      }
    );
  }

  reloadPage(): void {
    window.location.reload();
  }

  private submitTotp(request: () => any, onSuccess: (data: any) => void) {
    this.resetError();
    this.isSubmitting = true;
    request().subscribe(
      (data: any) => {
        this.isSubmitting = false;
        onSuccess(data);
      },
      err => this.showTotpError(err, 'Der Code ist falsch oder abgelaufen. Bitte versuchen Sie es erneut.')
    );
  }

  private completeLogin(authResponse: any) {
    if (!authResponse?.accessToken || !authResponse?.user) {
      this.error = true;
      this.errorMessage = 'Die Anmeldung konnte nicht abgeschlossen werden.';
      return;
    }

    this.tokenStorage.saveToken(authResponse.accessToken);
    this.tokenStorage.saveUser(authResponse.user);
    this.isLoginFailed = false;
    this.isLoggedIn = true;
    this.roles = this.tokenStorage.getUser()?.backendRoles || [];
    this.commonService.setUser({ email: this.tokenStorage.getUser().email });
    this.router.navigateByUrl('/admin/dashboard');
  }

  private getLoginErrorMessage(err: any): string {
    if (err?.error?.status === 'TOTP_NOT_CONFIGURED') {
      return 'Die Zwei-Faktor-Anmeldung ist derzeit nicht konfiguriert.';
    }
    return 'Die Zugangsdaten sind falsch. Bitte versuchen Sie es erneut.';
  }

  private showTotpError(err: any, fallback: string) {
    this.isSubmitting = false;
    this.error = true;
    this.errorMessage = fallback;
    if (err?.error?.status === 'TOTP_CODE_EXPIRED') {
      this.errorMessage = 'Die Anmeldung ist abgelaufen. Bitte kehren Sie zur Anmeldung zurück und melden Sie sich erneut an.';
      return;
    }
    if (err?.error?.status === 'TOTP_TOO_MANY_ATTEMPTS') {
      this.errorMessage = 'Zu viele Versuche. Bitte melden Sie sich erneut an.';
    }
  }

  private startExpiryCountdown(seconds: number) {
    this.clearExpiryTimer();
    this.expiresInSeconds = seconds;
    this.expiryTimer = setInterval(() => {
      this.expiresInSeconds = Math.max(this.expiresInSeconds - 1, 0);
      if (this.expiresInSeconds === 0) {
        this.clearExpiryTimer();
      }
    }, 1000);
  }

  private clearExpiryTimer() {
    if (this.expiryTimer) {
      clearInterval(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private resetError() {
    this.error = false;
    this.errorMessage = '';
  }
}
